// src/pages/UnsoldTicketWarehouseReport/modals/RecallWarehouseDetailModal.tsx
// Mục 8.f, README_UPDATE.md — modal chi tiết "Phiếu đã thẩm định & phê duyệt", mở rộng từ
// RecallAppraisalDetailModal.tsx (màn Duyệt thu hồi): thêm lịch sử đầy đủ giao vé → thu hồi →
// thẩm định (nguồn giao vé gốc, best-effort) + form gán/đổi vị trí kho lưu trữ ngay trong modal
// (đúng yêu cầu "phần thao tác chỉ cho view chi tiết" — cột ngoài bảng chỉ có nút Xem chi tiết).

import React, { useEffect, useState } from 'react';
import {
  Button,
  Col,
  Collapse,
  FormFeedback,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from 'reactstrap';
import { toast } from 'react-toastify';
import moment from 'moment';
import { AgentMap, getAgentCode, getAgentName } from 'helpers/agentLookup';
import { assignTicketRecallWarehouseLocationApi } from 'helpers/fakebackend_helper';
import { TICKET_RECALL_STATUS_COLORS, TICKET_RECALL_STATUS_LABELS, TicketRecall } from 'pages/TicketRecall/types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('unsold_ticket_warehouse_report');

interface RecallWarehouseDetailModalProps {
  isOpen: boolean;
  item: TicketRecall | null;
  agentMap: AgentMap;
  onClose: () => void;
  onSaved: () => void;
}

const formatNumber = (value?: number | null) =>
  typeof value === 'number' ? value.toLocaleString('vi-VN') : '0';

const formatDateTime = (value?: string | null) =>
  value ? moment(value).format('DD/MM/YYYY HH:mm') : '-';

const COLLAPSE_THRESHOLD = 10;
const MAX_LOCATION_LENGTH = 255;

const RecallWarehouseDetailModal: React.FC<RecallWarehouseDetailModalProps> = ({
  isOpen,
  item,
  agentMap,
  onClose,
  onSaved,
}) => {
  const lineCount = item?.lines?.length || 0;
  const [linesOpen, setLinesOpen] = useState(true);
  const [location, setLocation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setLinesOpen(lineCount <= COLLAPSE_THRESHOLD);
      setLocation(item?.warehouseLocation || '');
      setError(null);
    }
  }, [isOpen, lineCount, item?.warehouseLocation]);

  if (!item) return null;

  const statusColor = item.statusBadgeColor || TICKET_RECALL_STATUS_COLORS[String(item.status)] || 'secondary';
  const statusLabel = item.statusName || TICKET_RECALL_STATUS_LABELS[String(item.status)] || st('khong_ro_text');

  const handleSubmit = async () => {
    const trimmed = location.trim();
    if (!trimmed) {
      setError(st('vi_tri_kho_luu_tru_khong_text'));
      return;
    }
    if (trimmed.length > MAX_LOCATION_LENGTH) {
      setError(st('vi_tri_kho_luu_tru_toi_text', { max_location_length: MAX_LOCATION_LENGTH }));
      return;
    }

    setSubmitting(true);
    try {
      await assignTicketRecallWarehouseLocationApi(item.id, trimmed);
      toast.success(st('luu_vi_tri_kho_luu_tru_message'));
      onSaved();
    } catch (err: any) {
      toast.error(err?.message || st('khong_the_luu_vi_tri_kho_message'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>{st('chi_tiet_phieu_thu_hoi_title')} {item.recallCode}</ModalHeader>
      <ModalBody>
        <div className="table-responsive">
          <table className="table table-bordered table-sm mb-0">
            <tbody>
              <tr>
                <td width="30%" className="fw-semibold bg-light">
                  {st('ma_phieu_text')}
                </td>
                <td>{item.recallCode}</td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('trang_thai_text')}</td>
                <td>
                  <span className={`badge bg-${statusColor}-subtle text-${statusColor} text-uppercase`}>
                    {statusLabel}
                  </span>
                </td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('ke_hoach_phat_hanh_text')}</td>
                <td>{item.planCode || item.issuancePlanId}</td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('dai_ly_column')}</td>
                <td>
                  {item.agentId ? (
                    <>
                      {getAgentName(agentMap, item.agentId)}{' '}
                      <small className="text-muted">({getAgentCode(agentMap, item.agentId)})</small>
                    </>
                  ) : (
                    st('kho_tong_text')
                  )}
                </td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('tong_so_ve_text')}</td>
                <td className="fw-semibold">{formatNumber(item.totalQuantity)}</td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('phieu_giao_goc_text')}</td>
                <td>
                  {item.sourceDeliveryCodes && item.sourceDeliveryCodes.length > 0 ? (
                    item.sourceDeliveryCodes.map((code) => (
                      <span key={code} className="badge bg-secondary-subtle text-secondary me-1">
                        {code}
                      </span>
                    ))
                  ) : (
                    <span className="text-muted">{st('khong_xac_dinh_duoc_text')}</span>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Mục 8.f — dòng lịch sử "giao vé → thu hồi → thẩm định" */}
        <h6 className="mt-3 mb-2">{st('lich_su_phieu_title')}</h6>
        <div className="table-responsive">
          <table className="table table-bordered table-sm mb-0">
            <tbody>
              <tr>
                <td width="30%" className="fw-semibold bg-light">
                  {st('tao_de_xuat_text')}
                </td>
                <td>
                  {item.createdByName || '-'} — {formatDateTime(item.createdAt)}
                </td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('xac_nhan_cho_tham_dinh_text')}</td>
                <td>
                  {item.confirmedByName || '-'} — {formatDateTime(item.confirmedAt)}
                </td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('duyet_hint')}</td>
                <td>
                  {item.appraisedByName || '-'} — {formatDateTime(item.appraisedAt)}
                  {item.appraisalComment ? ` (${item.appraisalComment})` : ''}
                </td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('gan_vi_tri_kho_text')}</td>
                <td>
                  {item.warehouseAssignedAt
                    ? `${item.warehouseAssignedByName || '-'} — ${formatDateTime(item.warehouseAssignedAt)}`
                    : st('chua_gan_text')}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="d-flex align-items-center justify-content-between mt-3 mb-2">
          <h6 className="mb-0">{st('chi_tiet_dai_so_title')}{formatNumber(lineCount)} {st('dong_title')}</h6>
          <Button
            color="link"
            size="sm"
            className="p-0 text-decoration-none"
            type="button"
            aria-expanded={linesOpen}
            onClick={() => setLinesOpen((prev) => !prev)}
          >
            {linesOpen ? st('thu_gon_text') : st('mo_rong_text')}
            <i className={`${linesOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} align-bottom ms-1`}></i>
          </Button>
        </div>
        <Collapse isOpen={linesOpen}>
          <div className="table-responsive">
            <table className="table table-sm table-bordered mb-0">
              <thead className="table-light">
                <tr>
                  <th>{st('ky_hieu_column')}</th>
                  <th>{st('tu_so_column')}</th>
                  <th>{st('den_so_column')}</th>
                  <th className="text-end">{st('so_luong_column')}</th>
                </tr>
              </thead>
              <tbody>
                {lineCount === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center text-muted py-3">
                      {st('khong_co_dong_dai_so_text')}
                    </td>
                  </tr>
                ) : (
                  (item.lines || []).map((line, index) => (
                    <tr key={line.id ?? index}>
                      <td>{line.symbol}</td>
                      <td>{line.rangeStart}</td>
                      <td>{line.rangeEnd}</td>
                      <td className="text-end">{formatNumber(line.quantity)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Collapse>

        {/* Mục 8.f — gán/đổi vị trí kho ngay trong modal chi tiết */}
        <h6 className="mt-3 mb-2">{st('gan_vi_tri_kho_luu_tru_title')}</h6>
        <Row className="g-2 align-items-start">
          <Col md={8}>
            <Input
              type="text"
              placeholder={st('vd_kho_a_ke_02_thung_placeholder')}
              value={location}
              invalid={!!error}
              disabled={submitting}
              onChange={(event) => {
                setLocation(event.target.value);
                if (error) setError(null);
              }}
            />
            {error && <FormFeedback className="d-block">{error}</FormFeedback>}
          </Col>
          <Col md={4}>
            <Button color="primary" className="w-100" disabled={submitting} onClick={handleSubmit}>
              {submitting ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  {st('dang_luu_text')}
                </>
              ) : item.warehouseLocation ? (
                st('doi_vi_tri_text')
              ) : (
                st('gan_vi_tri_text')
              )}
            </Button>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose}>
          {st('dong_button')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default RecallWarehouseDetailModal;
